import { ThemeProvider as EmotionThemeProvider } from "@emotion/react";
import { ThemeProvider as MaterialThemeProvider } from "@mui/material";
import { light, lightDS3, dark, emerson } from "aviary-tokens/themes";
import type { AviaryTheme } from "aviary-tokens/themes";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";

import { useIsDS4Enabled } from "@aviary/hooks/useIsDS4Enabled";
import { usePreferredUserColorTheme } from "@aviary/hooks/usePreferredUserColorTheme";
import { BreakpointsProvider } from "@shared/context/BreakpointsContext";
import { isLocalStorageEnabled } from "@shared/helpers";

import { SnackWrap } from "../components/Snackbar";
import { Toaster } from "../components/Toast";

import { AviaryGlobalStyles } from "./AviaryGlobalStyles";
import { AviaryThemeContext, type ThemeTypes } from "./AviaryThemeContext";
import * as styles from "./materialTheme";

interface Props {
  /*
   * Toggles a white background instead of muted blue
   */
  isWhiteBackground?: boolean;
  /**
   * Basically the rest of the app
   *
   * @default undefined
   */
  children?: ReactNode;
  /*
   * Disables normalization (ONLY FOR USE WITHIN ADMIN)
   * SERIOUSLY, THIS PAINS ME TO ADD
   * Only pigeons can use this
   */
  noNormalization?: boolean;
  /**
   * Boolean to control if dark mode is enabled
   */
  isDarkMode?: boolean;
  /**
   * Boolean to enable Emerson theme instead of Fullscript
   * Doesn't work with dark mode
   */
  isEmerson?: boolean;
}

const getStoredTheme = () => {
  let storedTheme = null;
  if (typeof window !== "undefined" && isLocalStorageEnabled()) {
    storedTheme = localStorage.getItem("aviaryTheme");
  }

  return storedTheme;
};

const getInitialTheme = (isDarkMode, isDS4, isEmerson) => {
  if (isEmerson) {
    return emerson;
  }
  if ((isDS4 && isDarkMode) || (isDS4 && getStoredTheme() === "dark")) {
    return dark;
  }
  if (isDS4) {
    return light;
  }

  return lightDS3;
};

const AviaryProvider = ({
  isWhiteBackground,
  noNormalization,
  isDarkMode,
  isEmerson,
  children,
}: Props) => {
  const isDS4Enabled = useIsDS4Enabled();
  const [currentTheme, setCurrentTheme] = useState<AviaryTheme>(
    getInitialTheme(isDarkMode, isDS4Enabled, isEmerson)
  );

  const { isPrefersDark } = usePreferredUserColorTheme();

  useEffect(() => {
    if (isEmerson) {
      setCurrentTheme(emerson);
    }
    if (isDS4Enabled) {
      setCurrentTheme(light);
    }
    if ((isDS4Enabled && isDarkMode) || (isDS4Enabled && getStoredTheme() === "dark")) {
      setCurrentTheme(dark);
    }
    if (!isDS4Enabled) {
      setCurrentTheme(lightDS3);
    }
  }, [isDS4Enabled, isDarkMode, isEmerson, isPrefersDark]);

  const setTheme = (theme: ThemeTypes) => {
    if (!isDS4Enabled) {
      return null;
    }
    if (theme === "light") {
      document?.querySelector("html")?.setAttribute("data-theme", "light");
      setCurrentTheme(light);
      localStorage.setItem("aviaryTheme", "light");
    }
    if (theme === "dark") {
      document?.querySelector("html")?.setAttribute("data-theme", "dark");
      setCurrentTheme(dark);
      localStorage.setItem("aviaryTheme", "dark");
    }
  };

  /* NOTE: Material Theme provider must be rendered FIRST, as it
   * also utilizes emotion to theme itself. If this is not done,
   * theming breaks during runtime in some MUI elements
   *
   * Documentation can be found under the interoperability on MUI:
   * https://mui.com/material-ui/guides/interoperability/#theme
   */
  return (
    <BreakpointsProvider>
      <MaterialThemeProvider theme={styles.materialAviaryTheme(currentTheme)}>
        <EmotionThemeProvider theme={currentTheme}>
          <AviaryThemeContext.Provider value={{ setTheme }}>
            <AviaryGlobalStyles
              isWhiteBackground={isWhiteBackground}
              noNormalization={noNormalization}
            />
            <SnackWrap>
              <Toaster>
                <>{children}</>
              </Toaster>
            </SnackWrap>
          </AviaryThemeContext.Provider>
        </EmotionThemeProvider>
      </MaterialThemeProvider>
    </BreakpointsProvider>
  );
};

export { AviaryProvider };
